import { cloneDeep } from "lodash";
import { useCallback, useEffect, useRef } from "react";
import { useBlocker } from "react-router-dom";

import { useConfirm } from "./useAlert";

const prompt = {
  title: "Discard unsaved changes?",
  children: "If you leave this page all unsaved changes will be lost.",
  okCaption: "Discard",
  okVariant: "primary",
  cancelCaption: "Keep editing",
  mixpanelName: "Leave page with unsaved changes",
} as const;

// If you want check if it's safe to navigate away from a page or if the content is dirty use this use this useBaseline function so set the correct refs for comparison. This function also has a markClean property that allows you to mark stuff as clean if you're using useIsDirty and assert that something is in fact clean after saving. All these functions below are much happier and reliable when separated out so don't ask for them all to be combined into one

export default function useBaselineRef<T>(current: T) {
  const baselineRef = useRef(cloneDeep(current));

  const markClean = (value: T) => {
    baselineRef.current = cloneDeep(value);
  };

  return { baselineRef, markClean };
}

//Use useIsDirty to check for unsaved changes  before executing an action that has no navigation involved (E.g organisation repopulation )to ensure a form is saved and clean before executing the next action.
export function useIsDirty<T>(current: T, baselineRef: React.RefObject<T>) {
  return !isEqual(baselineRef.current, current);
}

//Use useNavigationBlocker to check for unsaved changes when you want to navigate away fromm the page. useBlocker only checks when navigating, which is why useIsDirty is separate
export function useNavigationBlocker<T>(current: T, baselineRef: React.RefObject<T>, isSaving?: boolean) {
  const currentRef = useRef(current);
  currentRef.current = current;
  const isSavingRef = useRef(isSaving);
  isSavingRef.current = isSaving;

  const shouldBlock = useCallback(
    () => !isSavingRef.current && !isEqual(baselineRef.current, currentRef.current),
    [baselineRef],
  );

  const blocker = useBlocker(shouldBlock);
  const confirm = useConfirm();

  useEffect(() => {
    if (blocker.state === "blocked") {
      const handle = async () => {
        const result = await confirm(prompt);

        if (result) {
          blocker.proceed();
        } else {
          blocker.reset();
        }
      };

      void handle();
    }
  }, [blocker, confirm]);
  return blocker;
}

export function isEqual<T>(a: T, b: T, path = ""): boolean {
  // If they're reference equal there's no need to go deeper.
  if (a === b) return true;
  if (Array.isArray(a)) {
    if (!Array.isArray(b) || b.length !== a.length) {
      console.log("Unsaved changes:", a, "changed to", b, "at", path);
      return false;
    }
    for (let i = 0; i < a.length; ++i) if (!isEqual(a[i], b[i], `${path}[${i}]`)) return false;
    return true;
  }
  if (Array.isArray(b)) {
    console.log("Unsaved changes:", a, "changed to", b, "at", path);
    return false;
  }
  if (a && typeof a === "object") {
    if (!b || typeof b !== "object") {
      console.log("Unsaved changes:", a, "changed to", b, "at", path);
      return false;
    }
    for (const key in { ...a, ...b }) if (!isEqual(a[key], b[key], `${path}.${key}`)) return false;
    return true;
  }
  // Normal triple-equals here would say NaN != NaN which isn't what we want here
  if (!Object.is(a, b)) {
    console.log("Unsaved changes:", a, "changed to", b, "at", path);
    return false;
  }
  return true;
}
